import { useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import './ContactPage.css';

const HELP_OPTIONS = [
  'General scrap enquiry',
  'Get a scrap quote',
  'Book a scrap collection',
  'Business scrap account',
  'Something else',
];

function UserIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <circle cx="12" cy="8" r="3.2" stroke="currentColor" strokeWidth="1.6" />
      <path d="M5.5 19c1.5-3 3.8-4.5 6.5-4.5s5 1.5 6.5 4.5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
    </svg>
  );
}

function MailIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <rect x="4" y="6" width="16" height="12" rx="2" stroke="currentColor" strokeWidth="1.6" />
      <path d="M5 8l7 5 7-5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
    </svg>
  );
}

function PhoneIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d="M8 4.5h2.2l1.2 3-1.6 1.2a10 10 0 0 0 5.5 5.5l1.2-1.6 3 1.2V18a1.5 1.5 0 0 1-1.5 1.5A12.5 12.5 0 0 1 6.5 7 1.5 1.5 0 0 1 8 4.5z"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function PinIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M12 20s5.5-4.8 5.5-9a5.5 5.5 0 1 0-11 0c0 4.2 5.5 9 5.5 9z" stroke="currentColor" strokeWidth="1.6" />
      <circle cx="12" cy="11" r="2" stroke="currentColor" strokeWidth="1.6" />
    </svg>
  );
}

function HelpIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <circle cx="12" cy="12" r="8" stroke="currentColor" strokeWidth="1.6" />
      <path d="M9.8 9.5a2.2 2.2 0 1 1 3.5 1.7c-.7.6-1.3 1.1-1.3 2.1" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
      <circle cx="12" cy="16.2" r="0.9" fill="currentColor" />
    </svg>
  );
}

function ExternalIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M10 6h8v8" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
      <path d="M18 6L9 15" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
      <path d="M7 8v9h9" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function ClockIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <circle cx="12" cy="12" r="8" stroke="currentColor" strokeWidth="1.6" />
      <path d="M12 8v4.2l2.6 1.5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
    </svg>
  );
}

function ShieldIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M12 4l7 2.5v5c0 4-2.8 7-7 9-4.2-2-7-5-7-9v-5L12 4z" stroke="currentColor" strokeWidth="1.6" />
    </svg>
  );
}

function StarIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M12 4.5l2.2 4.5 5 .7-3.6 3.5.9 5L12 16.2 7.5 18.2l.9-5L4.8 9.7l5-.7L12 4.5z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" />
    </svg>
  );
}

function SendIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M4 11.5L20 4l-5.5 16-3.2-6.3L4 11.5z" stroke="currentColor" strokeWidth="1.7" strokeLinejoin="round" />
    </svg>
  );
}

const WHY = [
  { label: 'Quick response times', Icon: ClockIcon },
  { label: 'Secure contact form', Icon: ShieldIcon },
  { label: 'Friendly support team', Icon: UserIcon },
  { label: '5-star rated service', Icon: StarIcon },
];

export default function ContactPage() {
  const [message, setMessage] = useState('');
  const [files, setFiles] = useState([]);
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef(null);

  function handleFiles(fileList) {
    const next = Array.from(fileList || []).filter((file) => file.type.startsWith('image/'));
    setFiles((prev) => [...prev, ...next].slice(0, 6));
  }

  function handleSubmit(event) {
    event.preventDefault();
  }

  return (
    <div className="contact-page">
      <section className="contact-hero" aria-labelledby="contact-heading">
        <div className="contact-hero-inner">
          <h1 id="contact-heading">Get in Touch</h1>
          <p>
            Whether you need a scrap quote, have questions, or want to book a collection -
            we&apos;re here to help
          </p>
        </div>
      </section>

      <section className="contact-body" aria-label="Contact form and details">
        <div className="contact-body-inner">
          <form className="contact-form-card" onSubmit={handleSubmit}>
            <div className="contact-row">
              <label className="contact-field">
                <span>
                  Name <em>*</em>
                </span>
                <span className="contact-input">
                  <UserIcon />
                  <input name="name" type="text" placeholder="Your full name" required />
                </span>
              </label>

              <label className="contact-field">
                <span>
                  Email <em>*</em>
                </span>
                <span className="contact-input">
                  <MailIcon />
                  <input name="email" type="email" placeholder="your@email.com" required />
                </span>
              </label>
            </div>

            <div className="contact-row">
              <label className="contact-field">
                <span>
                  Phone <em>*</em>
                </span>
                <span className="contact-input">
                  <PhoneIcon />
                  <input name="phone" type="tel" placeholder="Your phone number" required />
                </span>
              </label>

              <label className="contact-field">
                <span>Postcode</span>
                <span className="contact-input">
                  <PinIcon />
                  <input name="postcode" type="text" placeholder="e.g. SW1A 1AA" />
                </span>
              </label>
            </div>

            <label className="contact-field">
              <span>
                How can we help? <em>*</em>
              </span>
              <span className="contact-input contact-select-wrap">
                <HelpIcon />
                <select name="reason" defaultValue="" required>
                  <option value="" disabled>
                    Select a reason...
                  </option>
                  {HELP_OPTIONS.map((option) => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
                </select>
              </span>
            </label>

            <label className="contact-field">
              <span>
                Your Message <em>*</em>
              </span>
              <textarea
                name="message"
                placeholder="Tell us what scrap you need collected..."
                maxLength={1000}
                rows={6}
                required
                value={message}
                onChange={(event) => setMessage(event.target.value)}
              />
              <small className="char-count">{message.length} / 1000 characters</small>
            </label>

            <div className="contact-field">
              <span>Attach Images (Optional)</span>
              <div
                className={`contact-dropzone ${dragOver ? 'is-over' : ''}`}
                onDragOver={(event) => {
                  event.preventDefault();
                  setDragOver(true);
                }}
                onDragLeave={() => setDragOver(false)}
                onDrop={(event) => {
                  event.preventDefault();
                  setDragOver(false);
                  handleFiles(event.dataTransfer.files);
                }}
              >
                <p>
                  Drag &amp; Drop your files or{' '}
                  <button type="button" onClick={() => fileInputRef.current?.click()}>
                    Browse
                  </button>
                </p>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  multiple
                  hidden
                  onChange={(event) => handleFiles(event.target.files)}
                />
              </div>
              {files.length > 0 && (
                <ul className="contact-files">
                  {files.map((file) => (
                    <li key={`${file.name}-${file.lastModified}`}>{file.name}</li>
                  ))}
                </ul>
              )}
            </div>

            <label className="contact-check">
              <input type="checkbox" name="marketing" />
              <span>Email me news and special offers</span>
            </label>

            <button type="submit" className="contact-submit">
              Send Message
              <SendIcon />
            </button>
          </form>

          <aside className="contact-sidebar">
            <div className="contact-side-card">
              <h2>Other Ways to Reach Us</h2>
              <a className="reach-row" href="mailto:cs@snapscrap.co.uk">
                <span className="reach-icon tone-purple">
                  <MailIcon />
                </span>
                <span>
                  <strong>Email</strong>
                  <span>cs@snapscrap.co.uk</span>
                </span>
                <ExternalIcon />
              </a>
              <a
                className="reach-row"
                href="https://wa.me/447407500501"
                target="_blank"
                rel="noopener noreferrer"
              >
                <span className="reach-icon tone-green">
                  <PhoneIcon />
                </span>
                <span>
                  <strong>WhatsApp Chat</strong>
                  <span>07407500501</span>
                </span>
                <ExternalIcon />
              </a>
            </div>

            <div className="contact-side-card">
              <h2>Business Hours</h2>
              <div className="hours-row">
                <span>Monday - Sunday</span>
                <strong>6:00 AM - 10:00 PM</strong>
              </div>
              <div className="hours-note">
                <ClockIcon />
                <span>We&apos;ll normally reply within an hour</span>
              </div>
            </div>

            <div className="contact-side-card">
              <h2>Why Contact Us</h2>
              <ul className="why-list">
                {WHY.map(({ label, Icon }) => (
                  <li key={label}>
                    <Icon />
                    {label}
                  </li>
                ))}
              </ul>
            </div>

            <div className="contact-side-card">
              <h2>Quick Links</h2>
              <Link className="quick-link" to="/services">
                View Pricing Online
                <ExternalIcon />
              </Link>
            </div>
          </aside>
        </div>
      </section>
    </div>
  );
}
